import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/feature/PageMeta';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/categories';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20 px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center py-20">
              <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-damage-line text-2xl text-foreground-500"></i>
              </div>
              <h1 className="text-xl font-semibold text-foreground-950 mb-2">文章未找到</h1>
              <p className="text-sm text-foreground-600 mb-6">请检查链接是否正确，或返回首页浏览更多内容</p>
              <Link to="/" className="inline-flex px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = getRelatedArticles(article.slug, article.category, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: {
      '@type': 'Organization',
      name: '奶水乐园',
      url: 'https://naishuileyuan.com',
    },
    publisher: {
      '@type': 'Organization',
      name: '奶水乐园',
      logo: {
        '@type': 'ImageObject',
        url: 'https://naishuileyuan.com/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://naishuileyuan.com/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://naishuileyuan.com' },
      ...(category ? [{ '@type': 'ListItem', position: 2, name: category.name, item: `https://naishuileyuan.com/category/${category.slug}` }] : []),
      { '@type': 'ListItem', position: category ? 3 : 2, name: article.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <PageMeta
        title={article.title}
        description={article.excerpt}
        keywords={`${article.title},母乳喂养,奶水乐园,${category?.name || ''}`}
        canonicalUrl={`https://naishuileyuan.com/article/${article.slug}`}
        ogImage={article.coverImage}
        ogType="article"
        publishedTime={article.publishDate}
      />
      <SchemaOrg data={articleSchema} />
      <SchemaOrg data={breadcrumbSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
                { label: article.title },
              ]}
            />

            {/* Article Header */}
            <article className="py-8">
              <header>
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors mb-4 whitespace-nowrap cursor-pointer"
                  >
                    <i className={`${category.icon} text-xs`}></i>
                    {category.name}
                  </Link>
                )}
                <h1 className="text-xl md:text-3xl font-bold text-foreground-950 mb-4 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                  {article.title}
                </h1>
                <div className="flex items-center gap-3 text-sm text-foreground-500 mb-6">
                  <span><i className="ri-time-line mr-1"></i>{article.readTime} 分钟阅读</span>
                  <span>·</span>
                  <span><i className="ri-calendar-line mr-1"></i>{article.publishDate}</span>
                  <span>·</span>
                  <span>奶水乐园</span>
                </div>
              </header>

              {/* Cover Image */}
              <div className="w-full h-64 md:h-96 rounded-xl overflow-hidden mb-8">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Article Content */}
              <div
                className="prose prose-sm md:prose-base max-w-none
                  prose-headings:text-foreground-950 prose-headings:font-semibold
                  prose-p:text-foreground-700 prose-p:leading-relaxed
                  prose-li:text-foreground-700
                  prose-strong:text-foreground-900
                  prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700
                  [&_h2]:text-lg [&_h2]:md:text-xl [&_h2]:mt-8 [&_h2]:mb-4
                  [&_h3]:text-base [&_h3]:md:text-lg [&_h3]:mt-6 [&_h3]:mb-3
                  [&_p]:mb-4
                  [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-5
                  [&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:pl-5
                  [&_li]:mb-1.5"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Article Footer */}
              <div className="mt-10 pt-6 border-t border-background-200/60">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm text-foreground-600">标签：</span>
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="text-xs px-3 py-1 rounded-full bg-background-200 text-foreground-700 hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {category.name}
                    </Link>
                  )}
                  <span className="text-xs px-3 py-1 rounded-full bg-background-200 text-foreground-700 whitespace-nowrap">
                    母乳喂养
                  </span>
                  <span className="text-xs px-3 py-1 rounded-full bg-background-200 text-foreground-700 whitespace-nowrap">
                    奶水乐园
                  </span>
                </div>
              </div>
            </article>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="pb-14 md:pb-20">
                <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all cursor-pointer"
                    >
                      <div className="h-40 overflow-hidden">
                        <img
                          src={ra.coverImage}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-xs text-foreground-500">{ra.readTime} 分钟</span>
                          <span className="text-xs text-foreground-400">·</span>
                          <span className="text-xs text-foreground-500">{ra.publishDate}</span>
                        </div>
                        <h3 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                          {ra.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}